import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getStoneRequirement, listStoneQuotes, acceptStoneQuote, rejectStoneQuote,
  requestStoneQuoteRevision,
  updateStoneRequirement, deleteStoneRequirement, listStoneSuppliers, getImageUrl,
  formatApiError,
} from '@/utils/api';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import CollapsibleLayout from '@/components/CollapsibleLayout';
import StoneTabs from '@/components/stones/StoneTabs';
import StonesHeader from '@/components/stones/StonesHeader';
import StoneQuoteChat from '@/components/stones/StoneQuoteChat';
import ReviseQuoteDialog from '@/components/stones/ReviseQuoteDialog';
import { STONE_REQUIREMENT_STATUSES, STONE_QUOTE_STATUSES, STONE_KINDS, CERTIFICATE_TYPES } from '@/utils/constants';
import { ArrowLeft, Check, X, MessageSquare, Trash2, Loader2, Send, MessageCircle, Phone, AlertCircle, Gem } from 'lucide-react';

const REQ_STATUS = Object.fromEntries(STONE_REQUIREMENT_STATUSES.map(s => [s.value, s]));
const KIND = Object.fromEntries(STONE_KINDS.map(k => [k.value, k.label]));
const CERT_LABEL = Object.fromEntries(CERTIFICATE_TYPES.map(c => [c.value, c.label]));

// Build a wa.me URL that opens WhatsApp with a pre-filled message.
// phone: stripped of all non-digits then prefixed with country code if missing.
// fallback: India (91). The supplier's contact_phone is stored as user input,
// so we sanitize aggressively here rather than at create-time.
function buildWaLink(phone, message) {
  if (!phone) return null;
  let digits = String(phone).replace(/\D/g, '');
  if (!digits) return null;
  // If supplier saved as "9876543210" assume India; if "919876543210" use as-is.
  if (digits.length === 10) digits = '91' + digits;
  return `https://wa.me/${digits}?text=${encodeURIComponent(message)}`;
}

export default function StoneRequirementDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isSupplier = user?.role === 'stone_supplier';

  const [req, setReq] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openChatQuote, setOpenChatQuote] = useState(null);
  const [acting, setActing] = useState(null);
  const [reviseQuote, setReviseQuote] = useState(null);

  const fetch = async () => {
    try {
      const [r, q] = await Promise.all([
        getStoneRequirement(id),
        listStoneQuotes(id),
      ]);
      setReq(r.data.data);
      setQuotes(q.data.data || []);
      if (isAdmin) {
        const s = await listStoneSuppliers();
        setSuppliers(s.data.data || []);
      }
    } catch (e) { toast.error('Failed to load'); navigate('/stones'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetch(); /* eslint-disable-next-line */ }, [id]);

  if (loading) return <CollapsibleLayout><p className="text-sm text-center py-12">Loading…</p></CollapsibleLayout>;
  if (!req) return null;

  const status = REQ_STATUS[req.status] || { label: req.status, color: '' };
  const supplierName = (sid) => suppliers.find(s => s.id === sid)?.name || sid;

  const handleAccept = async (q) => {
    if (!confirm(`Accept this quote from ${q.supplier_name}? The requirement will be closed.`)) return;
    setActing(q.id);
    try { await acceptStoneQuote(q.id); toast.success('Quote accepted'); fetch(); }
    catch (e) { toast.error('Failed'); }
    finally { setActing(null); }
  };
  const handleReject = async (q) => {
    setActing(q.id);
    try { await rejectStoneQuote(q.id); toast.success('Quote rejected'); fetch(); }
    catch (e) { toast.error('Failed'); }
    finally { setActing(null); }
  };
  const handleRevise = async (payload) => {
    if (!reviseQuote) return;
    try {
      await requestStoneQuoteRevision(reviseQuote.id, payload);
      toast.success(`Revision requested from ${reviseQuote.supplier_name}`);
      setReviseQuote(null);
      fetch();
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to request revision'));
    }
  };
  const handleClose = async () => {
    if (!confirm('Close this requirement? Suppliers will no longer be able to submit quotes.')) return;
    try { await updateStoneRequirement(id, { status: 'closed' }); toast.success('Closed'); fetch(); }
    catch (e) { toast.error('Failed'); }
  };
  const handleDelete = async () => {
    if (!confirm('Delete this requirement and all its quotes/messages? This cannot be undone.')) return;
    try { await deleteStoneRequirement(id); toast.success('Deleted'); navigate('/stones'); }
    catch (e) { toast.error('Failed'); }
  };

  return (
    <CollapsibleLayout>
      <StoneTabs active="requirements" />
      <StonesHeader
        icon={Gem}
        title={req.requirement_no}
        subtitle={`${req.gemstone_type} · ${KIND[req.stone_kind] || req.stone_kind}${req.stone_kind === 'other' && req.stone_kind_other ? ` (${req.stone_kind_other})` : ''}`}
      >
        <Button variant="outline" size="sm" onClick={() => navigate('/stones')} className="bg-transparent border-slate-600 text-white hover:bg-slate-800 h-8 text-xs">
          <ArrowLeft size={13} className="mr-1" /> Back
        </Button>
        <span className={`text-[10px] px-2 py-0.5 rounded border ${status.color}`}>{status.label}</span>
        {isAdmin && req.status === 'open' && <Button size="sm" variant="outline" onClick={handleClose} className="bg-transparent border-slate-600 text-white hover:bg-slate-800 h-8 text-xs">Close RFQ</Button>}
        {isAdmin && (
          <Button size="sm" variant="ghost" className="text-red-300 hover:text-red-400 hover:bg-slate-800 h-8 w-8 p-0" onClick={handleDelete}>
            <Trash2 size={14} />
          </Button>
        )}
        {isSupplier && req.status === 'open' && (
          <Button onClick={() => navigate(`/stones/${id}/quote`)} className="bg-teal-600 hover:bg-teal-700 h-8 text-xs" data-testid="submit-quote-btn">
            <Send size={13} className="mr-1" /> {quotes.find(q => q.supplier_id === user.id) ? 'Edit Quote' : 'Submit Quote'}
          </Button>
        )}
      </StonesHeader>
      <div className="max-w-[1400px] mx-auto p-4 sm:p-6 space-y-4 sm:space-y-5">

        {/* Requirement summary */}
        <section className="bg-white rounded-lg border p-4 sm:p-5">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-x-4 gap-y-3 text-xs">
            <Field label="Size (mm)" value={req.size_mm} />
            <Field label="Dimensions L×B×H" value={req.dimensions ? [req.dimensions.l, req.dimensions.b, req.dimensions.h].filter(Boolean).join('×') : '—'} />
            <Field label="Ct / stone" value={req.ct_per_stone} />
            <Field label="Qty" value={req.qty} />
            <Field label="Total Ct" value={req.total_ctw} />
            <Field label="Certified" value={req.certified ? (CERT_LABEL[req.certificate_type] || 'Yes') : 'No'} />
            <Field label="Tracer" value={req.tracer ? 'Yes' : 'No'} />
            <Field label="Custom cut" value={req.custom_cut ? 'Yes' : 'No'} />
            <Field label="Deadline" value={req.deadline || '—'} />
            <Field label="Created by" value={req.created_by_name} />
          </div>
          {req.notes && (
            <div className="mt-3 pt-3 border-t">
              <Label>Notes</Label>
              <p className="text-xs text-slate-700 whitespace-pre-wrap mt-1">{req.notes}</p>
            </div>
          )}
          {req.sample_images?.length > 0 && (
            <div className="mt-3 pt-3 border-t">
              <Label>Sample Images</Label>
              <div className="flex gap-2 flex-wrap mt-2">
                {(req.sample_images || []).map((p, i) => (
                  <a key={i} href={getImageUrl(p)} target="_blank" rel="noreferrer">
                    <img src={getImageUrl(p)} alt="" className="w-20 h-20 object-cover rounded border" />
                  </a>
                ))}
              </div>
            </div>
          )}
        </section>

        {/* WhatsApp notify — admin-facing, prominent so notifications aren't missed.
            Always renders for admins viewing the detail, even when no suppliers are
            invited yet (shows an empty-state CTA). */}
        {isAdmin && (
          <section className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <MessageCircle size={18} className="text-emerald-700" />
              <h2 className="text-sm font-semibold text-emerald-900">Notify suppliers on WhatsApp</h2>
            </div>
            {!req.assigned_supplier_ids?.length ? (
              <div className="text-xs text-emerald-900/80 flex items-center gap-2 flex-wrap">
                <AlertCircle size={14} />
                No suppliers were invited to this requirement.
                <Button size="sm" variant="outline" className="h-7 text-xs ml-2"
                  onClick={() => navigate('/stones/suppliers')}>Manage suppliers</Button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {(req.assigned_supplier_ids || []).map(sid => {
                    const supp = suppliers.find(s => s.id === sid);
                    const reqUrl = `${window.location.origin}/stones/${req.id}`;
                    const waMsg = `Hi ${supp?.name || ''}, new stone requirement: ${req.requirement_no} — ${req.gemstone_type}` +
                                  `${req.size_mm ? ` ${req.size_mm}` : ''}${req.qty ? `, qty ${req.qty}` : ''}` +
                                  `${req.total_ctw ? `, total ${req.total_ctw} ct` : ''}. ` +
                                  `Please submit your quote: ${reqUrl}`;
                    const waLink = supp ? buildWaLink(supp.contact_phone, waMsg) : null;
                    if (!supp) {
                      return (
                        <div key={sid} className="bg-white border border-slate-200 rounded px-3 py-2 text-xs text-slate-500">
                          {sid} (unknown)
                        </div>
                      );
                    }
                    if (waLink) {
                      return (
                        <a key={sid} href={waLink} target="_blank" rel="noreferrer"
                          className="flex items-center justify-between bg-white border border-emerald-300 hover:bg-emerald-100 active:bg-emerald-200 rounded px-3 py-2 transition-colors"
                          data-testid={`wa-notify-${sid}`}>
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-slate-900 truncate">{supp.name}</div>
                            <div className="text-[11px] text-slate-500 flex items-center gap-1">
                              <Phone size={10} /> {supp.contact_phone}
                            </div>
                          </div>
                          <div className="flex items-center gap-1 text-emerald-700 font-medium text-xs whitespace-nowrap">
                            <MessageCircle size={14} /> Send
                          </div>
                        </a>
                      );
                    }
                    return (
                      <div key={sid} className="flex items-center justify-between bg-white border border-amber-200 rounded px-3 py-2">
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-slate-900 truncate">{supp.name}</div>
                          <div className="text-[11px] text-amber-700">No phone number on file</div>
                        </div>
                        <Button size="sm" variant="outline" className="h-7 text-xs"
                          onClick={() => navigate('/stones/suppliers')}>Add phone</Button>
                      </div>
                    );
                  })}
                </div>
                <p className="text-[10px] text-emerald-900/70 mt-3">
                  Each button opens WhatsApp with a pre-filled message and a deep link back to this requirement. One tap to send.
                </p>
              </>
            )}
          </section>
        )}

        {/* Quotes */}
        <section className="space-y-3">
          <h2 className="text-sm font-semibold">
            {isSupplier ? 'My Quote' : `Quotes (${quotes.length})`}
          </h2>
          {quotes.length === 0 ? (
            <div className="bg-white rounded-lg border p-6 text-center text-sm text-muted-foreground">
              No quotes yet.
            </div>
          ) : quotes.map(q => {
            const qstatus = STONE_QUOTE_STATUSES[q.status] || { label: q.status, color: '' };
            const chatOpen = openChatQuote === q.id;
            return (
              <div key={q.id} className="bg-white rounded-lg border" data-testid={`quote-${q.id}`}>
                <div className="p-4 flex flex-col md:flex-row md:items-start md:justify-between gap-3 border-b">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm">{q.supplier_name}</span>
                      {q.supplier_company && <span className="text-[10px] text-muted-foreground">({q.supplier_company})</span>}
                      <span className={`text-[10px] px-2 py-0.5 rounded border ${qstatus.color}`}>{qstatus.label}</span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                      Submitted {new Date(q.created_at).toLocaleString()}
                    </div>
                  </div>
                  <div className="text-right">
                    {q.price_total != null && (
                      <div className="text-lg font-bold text-teal-700">
                        {q.currency} {Number(q.price_total).toLocaleString()}
                      </div>
                    )}
                    {q.price_per_ct != null && (
                      <div className="text-[10px] text-muted-foreground">
                        {q.currency} {Number(q.price_per_ct).toLocaleString()} / ct
                      </div>
                    )}
                  </div>
                </div>
                <div className="p-4 grid grid-cols-2 md:grid-cols-5 gap-3 text-xs">
                  <Field label="Ct / stone" value={q.ct_per_stone_offered} />
                  <Field label="Qty" value={q.qty_offered} />
                  <Field label="Total CT" value={q.total_ctw_offered} />
                  <Field label="Currency" value={q.currency} />
                </div>
                {q.notes && (
                  <div className="px-4 pb-2 text-xs">
                    <Label>Supplier notes</Label>
                    <p className="text-slate-700 whitespace-pre-wrap mt-1">{q.notes}</p>
                  </div>
                )}
                {q.revise_request && q.status === 'revise_requested' && (
                  <div className="mx-4 mb-3 bg-violet-50 border border-violet-200 rounded-lg p-3">
                    <div className="text-[10px] uppercase tracking-wide text-violet-700 mb-1">Revision requested by admin</div>
                    {q.revise_request.target_price_per_ct != null && (
                      <div className="text-sm"><span className="font-medium">Target / ct:</span> {q.currency} {q.revise_request.target_price_per_ct}</div>
                    )}
                    {q.revise_request.target_price_total != null && (
                      <div className="text-sm"><span className="font-medium">Target total:</span> {q.currency} {Number(q.revise_request.target_price_total).toLocaleString()}</div>
                    )}
                    {q.revise_request.notes && (
                      <div className="text-xs text-slate-700 mt-1 whitespace-pre-wrap">{q.revise_request.notes}</div>
                    )}
                    <div className="text-[10px] text-slate-500 mt-2">
                      Asked by {q.revise_request.requested_by_name} on {new Date(q.revise_request.requested_at).toLocaleString()}
                      {q.revision_count ? ` · revision ${q.revision_count}` : ''}
                    </div>
                    {isSupplier && (
                      <Button size="sm" className="mt-2 bg-violet-600 hover:bg-violet-700" onClick={() => navigate(`/stones/${id}/quote`)} data-testid={`requote-${q.id}`}>
                        <Send size={14} className="mr-1" /> Submit revised quote
                      </Button>
                    )}
                  </div>
                )}
                {q.certificate_type && q.certificate_type !== 'none' && (
                  <div className="px-4 pb-2 text-xs">
                    <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Certificate: </span>
                    <span className="font-medium uppercase">{q.certificate_type}</span>
                    {q.certificate_number && <span className="text-slate-600"> · {q.certificate_number}</span>}
                  </div>
                )}
                {(q.stone_images?.length > 0 || q.certificate_image) && (
                  <div className="px-4 pb-3">
                    <Label>Stone images / Certificate</Label>
                    <div className="flex gap-2 flex-wrap mt-2">
                      {q.stone_images?.map((p, i) => (
                        <a key={i} href={getImageUrl(p)} target="_blank" rel="noreferrer">
                          <img src={getImageUrl(p)} alt="" className="w-20 h-20 object-cover rounded border" />
                        </a>
                      ))}
                      {q.certificate_image && (
                        <a href={getImageUrl(q.certificate_image)} target="_blank" rel="noreferrer">
                          <img src={getImageUrl(q.certificate_image)} alt="cert" className="w-20 h-20 object-cover rounded border-2 border-amber-300" title="Certificate" />
                        </a>
                      )}
                    </div>
                  </div>
                )}
                <div className="px-4 pb-3 flex items-center gap-2">
                  <Button size="sm" variant="ghost" onClick={() => setOpenChatQuote(chatOpen ? null : q.id)} data-testid={`chat-${q.id}`}>
                    <MessageSquare size={14} className="mr-1" />
                    {chatOpen ? 'Hide chat' : 'Negotiate'}
                  </Button>
                  {isAdmin && (q.status === 'submitted' || q.status === 'revise_requested') && (
                    <>
                      {q.status === 'submitted' && (
                        <>
                          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" disabled={acting === q.id} onClick={() => handleAccept(q)} data-testid={`accept-${q.id}`}>
                            {acting === q.id ? <Loader2 size={12} className="animate-spin mr-1" /> : <Check size={14} className="mr-1" />}
                            Accept
                          </Button>
                          <Button size="sm" variant="outline" className="text-violet-600 border-violet-200 hover:bg-violet-50" disabled={acting === q.id} onClick={() => setReviseQuote(q)} data-testid={`revise-${q.id}`}>
                            <Send size={14} className="mr-1" /> Revise
                          </Button>
                          <Button size="sm" variant="outline" className="text-red-500" disabled={acting === q.id} onClick={() => handleReject(q)} data-testid={`reject-${q.id}`}>
                            <X size={14} className="mr-1" /> Reject
                          </Button>
                        </>
                      )}
                      {q.status === 'revise_requested' && (
                        <span className="text-xs text-violet-700 italic">Awaiting supplier's revision…</span>
                      )}
                    </>
                  )}
                </div>
                {chatOpen && (
                  <div className="border-t p-4 bg-slate-50">
                    <StoneQuoteChat quoteId={q.id} currentUser={user} />
                  </div>
                )}
              </div>
            );
          })}
        </section>
      </div>

      <ReviseQuoteDialog
        open={!!reviseQuote}
        onOpenChange={(o) => { if (!o) setReviseQuote(null); }}
        quote={reviseQuote}
        onSubmit={handleRevise}
      />
    </CollapsibleLayout>
  );
}

const Label = ({ children }) => <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{children}</span>;
const Field = ({ label, value }) => (
  <div>
    <Label>{label}</Label>
    <div className="text-sm">{value ?? '—'}</div>
  </div>
);
